import { useMemo, useState } from 'react'
import type { AccessScope, CanvasElement } from '../../types/schema'
import type { WorkspaceRole } from '../../types/workspace'
import type { TimelineElement } from '../../types/timelineElement'
import { Button, Select, type SelectOption } from '../Common'
import { Tip } from '../Common/Hover/Tip'
import { useNls } from '../../nls/nlsProvider'
import { buildAccessScopeTree, setNodeGrant } from '../../utils/accessScopeTree'
import './accessScopePanel.css'

export interface AccessScopeEditorProps {
  value: AccessScope
  onChange(next: AccessScope): void
  role: WorkspaceRole
  elements: TimelineElement[] | CanvasElement[]
  disabled?: boolean
}

export const AccessScopeEditor = ({
  value,
  onChange,
  role,
  elements,
  disabled = false,
}: AccessScopeEditorProps) => {
  const { t } = useNls()
  const [overLimitMessage, setOverLimitMessage] = useState<string | null>(null)

  const isRoleViewOnly = role === 'VIEWER' || role === 'READONLY'

  const treeNodes = useMemo(() => {
    if (!value || value.mode !== 'RESTRICTED') return []
    return buildAccessScopeTree({
      elements: elements as unknown as Array<CanvasElement & Record<string, unknown>>,
      role,
      accessScope: value,
    })
  }, [elements, role, value])

  const handleModeChange = (mode: 'ALL' | 'RESTRICTED') => {
    if (disabled) return
    setOverLimitMessage(null)
    onChange({
      mode,
      grants: value?.grants ?? {},
    })
  }

  const handleGrantChange = (nodeId: string, grant: 'NONE' | 'VIEW' | 'EDIT') => {
    if (disabled || !value) return
    setOverLimitMessage(null)

    const next = setNodeGrant(value, nodeId, grant)
    if (next.overLimit) {
      setOverLimitMessage(t('NLSAccessScopeOverLimit'))
      return
    }
    onChange({
      mode: next.mode,
      grants: next.grants,
    })
  }

  const grantOptions: SelectOption<'NONE' | 'VIEW' | 'EDIT'>[] = [
    { value: 'NONE', label: t('NLSAccessScopeGrantNone') },
    { value: 'VIEW', label: t('NLSAccessScopeGrantView') },
    {
      value: 'EDIT',
      label: t('NLSAccessScopeGrantEdit'),
      disabled: isRoleViewOnly,
    },
  ]

  return (
    <div className="accessScopeEditor">
      <div
        className="accessScopeModeGroup"
        role="group"
        aria-label={t('NLSAccessScopeModeAria')}
      >
        <Button
          size="sm"
          variant={value.mode === 'ALL' ? 'primary' : 'secondary'}
          pressed={value.mode === 'ALL'}
          disabled={disabled}
          onClick={() => handleModeChange('ALL')}
        >
          {t('NLSAccessScopeModeAllGoals')}
        </Button>
        <Button
          size="sm"
          variant={value.mode === 'RESTRICTED' ? 'primary' : 'secondary'}
          pressed={value.mode === 'RESTRICTED'}
          disabled={disabled}
          onClick={() => handleModeChange('RESTRICTED')}
        >
          {t('NLSAccessScopeModeOnlyChosen')}
        </Button>
      </div>

      {value.mode === 'RESTRICTED' && (
        <div className="accessScopeTree" role="list" aria-label={t('NLSAccessScopeSectionTitle')}>
          {treeNodes.length === 0 ? (
            <div className="accessScopeEmpty">{t('NLSAccessScopeEmptyTree')}</div>
          ) : (
            treeNodes.map((node) => {
              const selectElement = (
                <Select<'NONE' | 'VIEW' | 'EDIT'>
                  trigger="pill"
                  value={node.ownGrant ?? 'NONE'}
                  options={grantOptions}
                  onChange={(val) => handleGrantChange(node.id, val)}
                  ariaLabel={t('NLSAccessScopeGrantAria', { title: node.title })}
                  disabled={disabled}
                  size="sm"
                  className="accessScopeSelect"
                />
              )

              return (
                <div
                  key={node.id}
                  className="accessScopeRow"
                  role="listitem"
                  style={{ paddingLeft: `${node.depth * 16}px` }}
                >
                  <div className="accessScopeNodeInfo">
                    <span className="accessScopeNodeTitle" title={node.title}>
                      {node.title}
                    </span>
                    {node.inheritedFrom && !node.ownGrant && (
                      <span className="accessScopeInherited">
                        ({t('NLSAccessScopeInheritedFrom', { parent: node.inheritedFrom.title })})
                      </span>
                    )}
                  </div>

                  <div className="accessScopeControls">
                    {isRoleViewOnly ? (
                      <Tip label={t('NLSAccessScopeCeilingTip')}>
                        <span className="accessScopeCeilingTip">
                          {selectElement}
                        </span>
                      </Tip>
                    ) : (
                      selectElement
                    )}
                  </div>
                </div>
              )
            })
          )}
        </div>
      )}

      {overLimitMessage && (
        <div className="accessScopeOverLimit" role="alert">
          {overLimitMessage}
        </div>
      )}
    </div>
  )
}
export default AccessScopeEditor
